import {
  useEffect,
  useState,
  useCallback,
  createContext,
  useContext,
  memo,
  useRef,
  useLayoutEffect,
  Children,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { IoIosArrowDown, IoIosArrowForward, IoIosSearch } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import mobToggleIcon from "../../assets/icons/sideToggle.svg";
import openSidebarIcon from "../../assets/new_icons/open.svg";
import closeSidebarIcon from "../../assets/new_icons/close.svg";
import AppIcon from "../../components/Common/AppIcon";
import SidebarFloatingTooltip from "./SidebarFloatingTooltip";
import SidebarFlyoutSubmenu from "./SidebarFlyoutSubmenu";
import MobileNavDrawer from "./MobileNavDrawer";
import {
  authSelector,
  clearLogoutAuthState,
  getNavOptionsData,
  logoutUser,
} from "../../components/Auth/AuthSlice";
import CloseButton from "../../components/Common/CloseButton";
import {
  canAccessNavbarItem,
  isMultiAccessUser,
  ROUTING_MENU_ACCESS_PATHS,
  ROUTING_SENDER_ACCESS_PATHS,
} from "../../components/User/Abac/abacAccess";
import {
  findFirstAllowedAbacRoute,
  isAbacRouteAllowed,
} from "../../components/User/Abac/abacRouteMap";
import { useFetch } from "../../hooks/useFetch";
import useAbacAccess from "../../hooks/useAbacAccess";
import {
  clearAuthStorage,
  checkUserRole,
  getItem,
  hasRoles,
  isArray,
  isManager,
  isSeller,
  someTrue,
  storeItem,
  showToast,
} from "../../lib/utility";
import { middleware, persistor } from "../../redux/store";
import { commonUtilitySelector } from "../../redux/commonUtilitySlice";
import "./Sidebar.scss";
import { getSmscNames } from "../../components/User/UserSlice";
import SidebarActivePill from "./SidebarActivePill";
import { useSidebarActivePill } from "./useSidebarActivePill";


const ROUTE_SECTION_MAP = [
  {
    section: "customerEngagement",
    match: (p) =>
      p.startsWith("/message") ||
      p.startsWith("/template") ||
      p.startsWith("/utilities/two-way") ||
      p.startsWith("/address-book") ||
      p.startsWith("/utilities/subscription") ||
      p.startsWith("/view"),
  },
  {
    section: "vendorsProviders",
    match: (p) => p.startsWith("/vendor") || p.startsWith("/smsc"),
  },
  {
    section: "organizationAccounts",
    match: (p) =>
      p.startsWith("/company") ||
      (p.startsWith("/user") && !isErrorCodesPath(p)) ||
      p.startsWith("/sales") ||
      p.startsWith("/sales-list") ||
      p.startsWith("/reports/user-status"),
  },
  {
    section: "messageOperations",
    match: (p) => p.startsWith("/dlt") || p.startsWith("/approvals"),
  },
  {
    section: "analyticsReports",
    match: (p) =>
      p.startsWith("/reports") && !p.startsWith("/reports/user-status"),
  },
  {
    section: "complianceSecurity",
    match: (p) =>
      p.startsWith("/firewall") ||
      p.startsWith("/network") ||
      p.startsWith("/utilities/pcaps"),
  },
  {
    section: "billingRouting",
    match: (p) => p.startsWith("/routing"),
  },
  {
    section: "developers",
    match: (p) =>
      isErrorCodesPath(p) ||
      p.startsWith("/utilities/converter") ||
      p.startsWith("/downloads") ||
      p.startsWith("/developer/sdk") ||
      p.startsWith("/api-runner"),
  },
];

const SECTION_TOGGLES = [
  "customerEngagement",
  "vendorsProviders",
  "organizationAccounts",
  "messageOperations",
  "analyticsReports",
  "complianceSecurity",
  "billingRouting",
  "developers",
];

const PATH_BASED_TOGGLES = new Set([
  "providers",
  "campaign",
  "summary",
  "detailed",
  "performance",
  "bulk",
  "approvalSender",
  "dlt",
  "user",
  "errorCode",
  "routing",
  "routingSender",
  "hlr",
  "view",
  "approvals",
  "utilities",
  "smsc",
  "sender",
  "report",
]);

/** All menus closed by default after login; user opens manually. */
const initialValue = {
  customerEngagement: false,
  vendorsProviders: false,
  providers: false,
  organizationAccounts: false,
  messageOperations: false,
  analyticsReports: false,
  complianceSecurity: false,
  billingRouting: false,
  developers: false,
  campaign: false,
  summary: false,
  detailed: false,
  performance: false,
  bulk: false,
  approvalSender: false,
  dlt: false,
  user: false,
  errorCode: false,
  routing: false,
  routingSender: false,
  view: false,
};

const SidebarContext = createContext();

/** Error Codes lives under /user/error-code but is NOT the Users module. */
const isErrorCodesPath = (pathname = "") => {
  const p = String(pathname);
  return (
    p.startsWith("/user/error-code") || p.startsWith("/smsc/error-code")
  );
};

/** Users sidebar item — list/limit/multi-access/abac only (never Error Codes). */
const isUsersModulePath = (pathname = "") => {
  const p = String(pathname);
  if (isErrorCodesPath(p)) return false;
  return (
    p === "/user" ||
    p === "/user/" ||
    p.startsWith("/user/list") ||
    p.startsWith("/user/limit") ||
    p.startsWith("/user/multi-access") ||
    p.startsWith("/user/abac")
  );
};

function updateSidebarTooltipPosition(e) {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty(
    "--sidebar-tooltip-top",
    `${rect.top + rect.height / 2}px`
  );
  e.currentTarget.style.setProperty(
    "--sidebar-tooltip-left",
    `${rect.left + rect.width / 2}px`
  );
  e.currentTarget.style.setProperty(
    "--sidebar-tooltip-edge-top",
    `${rect.top}px`
  );
}

/**
 * Name tooltip:
 * - Laptop + expanded sidebar → no name (color hover only)
 * - Laptop + collapsed sidebar → show name
 * - Mobile / tablet → show name
 */
function getSidebarTooltipProps(label, { isCollapsed, isCompact } = {}) {
  if (!label) return {};
  if (!isCollapsed && !isCompact) return {};
  return {
    "data-tooltip": label,
    "data-tooltip-location": isCollapsed ? "right" : "top",
    "data-sidebar-tooltip": "true",
    onMouseEnter: updateSidebarTooltipPosition,
    onMouseMove: updateSidebarTooltipPosition,
    onFocus: updateSidebarTooltipPosition,
  };
}

function renderSidebarIcon(icon) {
  if (!icon) return null;
  if (typeof icon === "string") {
    return <img loading="lazy" src={icon} alt="" />;
  }
  return icon;
}

const LinkItem = memo(
  ({
    name,
    icon,
    to,
    navOption,
    roleCondition,
    customValidation,
    abacPath,
    toggle,
    children,
    isNavActive,
  }) => {
    const location = useLocation();
    const {
      user,
      navOptions,
      abacPermissions,
      profileData,
      submenusOpen,
      toggleSubMenu,
      isCollapsed,
      isCompact,
      sidebarSearch,
      snapToElement,
      clearIntent,
      refreshPill,
      prefersReducedMotion,
      reanchorAfterSubmenuClose,
      showFloatingTooltip,
      hideFloatingTooltip,
      openFlyoutSubmenu,
      closeFlyoutSubmenu,
      activeFlyout,
    } = useContext(SidebarContext);

    let shouldRender = navOption ? hasRoles("superadmin,system", user) || !!navOptions?.[navOption] : true;
    if (roleCondition !== undefined)
      shouldRender = shouldRender && hasRoles(roleCondition, user);
    if (customValidation !== undefined)
      shouldRender = shouldRender && customValidation;
    if (shouldRender && (abacPath || to)) {
      shouldRender = isAbacRouteAllowed(
        to ? `/${to}` : "",
        abacPermissions,
        user,
        profileData,
        abacPath
      );
    }

    const search = String(sidebarSearch || "").trim().toLowerCase();
    const nameMatch = !search || String(name || "").toLowerCase().includes(search);

    if (!shouldRender) return null;

    // Search: leaf items must match; parents without match still render matching children
    if (search && !nameMatch && to) return null;
    if (search && !nameMatch && !children) return null;
    if (search && !nameMatch && children) {
      return <>{children}</>;
    }

    const submenuOpen = search || !!submenusOpen[toggle];

    // Collapsed multi-level: extract valid child items for flyout panel
    if (isCollapsed && children && !to) {
      const childArray = Children.toArray(children);
      const validChildItems = childArray
        .map((child) => {
          if (!child || !child.props) return null;
          const {
            name: cName,
            to: cTo,
            icon: cIcon,
            navOption: cNavOption,
            roleCondition: cRoleCondition,
            customValidation: cCustomValidation,
            abacPath: cAbacPath,
            isNavActive: cIsNavActive,
          } = child.props;

          let allowed = cNavOption ? hasRoles("superadmin,system", user) || !!navOptions?.[cNavOption] : true;
          if (cRoleCondition !== undefined)
            allowed = allowed && hasRoles(cRoleCondition, user);
          if (cCustomValidation !== undefined)
            allowed = allowed && cCustomValidation;
          if (allowed && (cAbacPath || cTo)) {
            allowed = isAbacRouteAllowed(
              cTo ? `/${cTo}` : "",
              abacPermissions,
              user,
              profileData,
              cAbacPath
            );
          }
          if (!allowed) return null;
          return {
            name: cName,
            to: cTo,
            icon: cIcon,
            isNavActive: cIsNavActive,
          };
        })
        .filter(Boolean);

      if (validChildItems.length === 0) return null;

      const currentPath = location.pathname || "";
      const isAnyChildActive = validChildItems.some((item) => {
        if (typeof item.isNavActive === "function") {
          return item.isNavActive(currentPath);
        }
        return (
          item.to &&
          (currentPath === `/${item.to}` ||
            currentPath.startsWith(`/${item.to}/`))
        );
      });

      const isFlyoutOpen = activeFlyout?.id === toggle;

      return (
        <div className="sidebar-link-wrap">
          <button
            type="button"
            className={`link collapsed multi-level ${
              isAnyChildActive ? "active" : ""
            } ${isFlyoutOpen ? "flyout-open" : ""}`}
            aria-haspopup="menu"
            aria-expanded={isFlyoutOpen}
            aria-controls={`sidebar-flyout-${toggle}`}
            aria-label={name}
            onMouseEnter={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              openFlyoutSubmenu(
                {
                  id: toggle,
                  title: name,
                  icon,
                  items: validChildItems,
                  triggerEl: e.currentTarget,
                  triggerRect: rect,
                },
                false
              );
            }}
            onMouseLeave={() => closeFlyoutSubmenu(false)}
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              openFlyoutSubmenu(
                {
                  id: toggle,
                  title: name,
                  icon,
                  items: validChildItems,
                  triggerEl: e.currentTarget,
                  triggerRect: rect,
                },
                true
              );
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" ||
                e.key === " " ||
                e.key === "ArrowRight"
              ) {
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                openFlyoutSubmenu(
                  {
                    id: toggle,
                    title: name,
                    icon,
                    items: validChildItems,
                    triggerEl: e.currentTarget,
                    triggerRect: rect,
                  },
                  true
                );
              }
            }}
          >
            {renderSidebarIcon(icon)}
            <span
              className="sidebar-multi-level-indicator"
              aria-hidden="true"
            >
              <IoIosArrowForward />
            </span>
          </button>
        </div>
      );
    }

    return (
      <>
        {to ? (
          <div className="sidebar-link-wrap">
            <NavLink
              to={"/" + to}
              end={
                to === "user" ||
                to === "user/list" ||
                to === "user/error-code" ||
                to === "user/limit" ||
                to === "smsc/error-code" ||
                (typeof isNavActive === "function" && !String(to).includes("/"))
              }
              className={({ isActive }) => {
                const path = location.pathname || "";
                let active;
                if (typeof isNavActive === "function") {
                  active = !!isNavActive(path);
                } else if (
                  to === "user" ||
                  to === "user/list" ||
                  to === "user/limit"
                ) {
                  active = isUsersModulePath(path);
                } else if (
                  to === "user/error-code" ||
                  to === "smsc/error-code" ||
                  String(to || "").includes("error-code")
                ) {
                  active = isErrorCodesPath(path);
                } else {
                  active = !!isActive;
                }
                if (
                  (to === "user" || to === "user/list" || to === "user/limit") &&
                  isErrorCodesPath(path)
                ) {
                  active = false;
                }
                return (
                  "link" +
                  (isCollapsed ? " collapsed" : "") +
                  (active ? " active" : "")
                );
              }}
              aria-current={(() => {
                const path = location.pathname || "";
                if (
                  (to === "user" || to === "user/list" || to === "user/limit") &&
                  isErrorCodesPath(path)
                ) {
                  return undefined;
                }
                if (typeof isNavActive === "function") {
                  return isNavActive(path) ? "page" : undefined;
                }
                if (to === "user" || to === "user/list" || to === "user/limit") {
                  return isUsersModulePath(path) ? "page" : undefined;
                }
                if (
                  to === "user/error-code" ||
                  to === "smsc/error-code" ||
                  String(to || "").includes("error-code")
                ) {
                  return isErrorCodesPath(path) ? "page" : undefined;
                }
                return undefined;
              })()}
              onClick={(e) => {
                if (String(to || "").includes("error-code")) {
                  clearIntent();
                }
                snapToElement(e.currentTarget);
              }}
              onMouseEnter={(e) => {
                if (isCollapsed) showFloatingTooltip(name, e.currentTarget);
              }}
              onMouseLeave={() => {
                if (isCollapsed) hideFloatingTooltip();
              }}
              onFocus={(e) => {
                if (isCollapsed) showFloatingTooltip(name, e.currentTarget);
              }}
              onBlur={() => {
                if (isCollapsed) hideFloatingTooltip();
              }}
            >
              {renderSidebarIcon(icon)}
              <span
                className="text-light-gray sidebar-link-label"
                title={!isCollapsed ? name : undefined}
              >
                {name}
              </span>
            </NavLink>
          </div>
        ) : (
          children && (
            <div className="dropdown">
              <div className="sidebar-link-wrap">
                <div
                  className="link"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (search) return;
                    clearIntent();
                    toggleSubMenu(toggle);
                    refreshPill();
                  }}
                  onMouseEnter={(e) => {
                    if (isCollapsed) showFloatingTooltip(name, e.currentTarget);
                  }}
                  onMouseLeave={() => {
                    if (isCollapsed) hideFloatingTooltip();
                  }}
                  onFocus={(e) => {
                    if (isCollapsed) showFloatingTooltip(name, e.currentTarget);
                  }}
                  onBlur={() => {
                    if (isCollapsed) hideFloatingTooltip();
                  }}
                >
                  {renderSidebarIcon(icon)}
                  <span
                    className="text-light-gray sidebar-link-label"
                    title={!isCollapsed ? name : undefined}
                  >
                    {name}
                  </span>
                  {submenuOpen ? <IoIosArrowDown /> : <IoIosArrowForward />}
                </div>
              </div>
              <AnimatePresence
                initial={false}
                onExitComplete={() => reanchorAfterSubmenuClose(toggle)}
              >
                {submenuOpen && (
                  <motion.div
                    className="submenu"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={
                      prefersReducedMotion
                        ? { duration: 0 }
                        : { duration: 0.22, ease: "easeOut" }
                    }
                    style={{ overflow: "hidden" }}
                    onAnimationComplete={() => {
                      if (submenuOpen) refreshPill();
                    }}
                  >
                    {children}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        )}
      </>
    );
  }
);

/** True when section body has at least one rendered nav row (link or expandable parent). */
const sectionHasVisibleNav = (root) => {
  if (!root) return false;
  return !!root.querySelector(
    "a.link, .link, .sidebar-link-wrap > .link, .sidebar-link-wrap > a, [role='link']"
  );
};

const MenuSection = memo(({ title, toggle, icon: groupIcon, children }) => {
  const {
    submenusOpen,
    toggleSubMenu,
    isCollapsed,
    isCompact,
    sidebarSearch,
    prefersReducedMotion,
    refreshPill,
    clearIntent,
    showFloatingTooltip,
    hideFloatingTooltip,
  } = useContext(SidebarContext);

  const bodyRef = useRef(null);
  const [hasVisibleItems, setHasVisibleItems] = useState(
    () => Children.count(children) > 0
  );

  const isOpen = !!submenusOpen[toggle];
  const search = String(sidebarSearch || "").trim();
  const showBody = isCollapsed ? isOpen : isOpen || !!search;

  const syncVisibility = useCallback(() => {
    const has = sectionHasVisibleNav(bodyRef.current);
    setHasVisibleItems((prev) => (prev === has ? prev : has));
  }, []);

  useLayoutEffect(() => {
    syncVisibility();
  });

  useLayoutEffect(() => {
    const root = bodyRef.current;
    if (!root || typeof MutationObserver === "undefined") return undefined;
    const mo = new MutationObserver(() => syncVisibility());
    mo.observe(root, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [syncVisibility]);


  return (
    <div
      className={
        "sidebar-section dropdown" +
        (search ? " is-searching" : "") +
        (hasVisibleItems ? "" : " is-empty") +
        (isCollapsed ? " collapsed-section" : "")
      }
      id={`sidebar-section-${toggle}`}
      hidden={!hasVisibleItems}
      aria-hidden={!hasVisibleItems}
    >
      {isCollapsed ? (
        <button
          type="button"
          className={
            "sidebar-section-header collapsed-tile" + (isOpen ? " open" : "")
          }
          onClick={(e) => {
            e.stopPropagation();
            clearIntent();
            toggleSubMenu(toggle);
          }}
          onMouseEnter={(e) => showFloatingTooltip(title, e.currentTarget)}
          onMouseLeave={hideFloatingTooltip}
          onFocus={(e) => showFloatingTooltip(title, e.currentTarget)}
          onBlur={hideFloatingTooltip}
          aria-label={title}
          aria-expanded={isOpen}
          aria-controls={`section-body-${toggle}`}
        >
          {groupIcon && (
            <span className="sidebar-section-icon" aria-hidden="true">
              {typeof groupIcon === "string" ? (
                <AppIcon name={groupIcon} size={20} />
              ) : (
                renderSidebarIcon(groupIcon)
              )}
            </span>
          )}
          <span
            className={
              "sidebar-section-mini-indicator" + (isOpen ? " is-open" : "")
            }
            aria-hidden="true"
          >
            <IoIosArrowDown />
          </span>
        </button>
      ) : (
        <button
          type="button"
          className={
            "sidebar-section-header" + (isOpen || search ? " open" : "")
          }
          onClick={(e) => {
            e.stopPropagation();
            if (search || !hasVisibleItems) return;
            clearIntent();
            toggleSubMenu(toggle);
            refreshPill();
            requestAnimationFrame(() => refreshPill());
            setTimeout(() => refreshPill(), 240);
          }}
          aria-expanded={isOpen || !!search}
          aria-controls={`section-body-${toggle}`}
        >
          <span className="sidebar-section-title">{title}</span>
          <span
            className={
              "sidebar-section-chevron" + (isOpen || search ? " open" : "")
            }
            aria-hidden="true"
          >
            <IoIosArrowDown />
          </span>
        </button>
      )}

      <motion.div
        id={`section-body-${toggle}`}
        ref={bodyRef}
        className="sidebar-section-body"
        initial={false}
        animate={
          prefersReducedMotion
            ? { height: showBody ? "auto" : 0, opacity: showBody ? 1 : 0 }
            : showBody
            ? { height: "auto", opacity: 1 }
            : { height: 0, opacity: 0 }
        }
        transition={
          prefersReducedMotion
            ? { duration: 0 }
            : { duration: 0.24, ease: "easeOut" }
        }
        style={{ overflow: "hidden" }}
        onAnimationComplete={() => {
          if (hasVisibleItems && !isCollapsed) refreshPill();
        }}
      >
        {children}
      </motion.div>
    </div>
  );
});

const icon = (name) => <AppIcon name={name} />;

const Sidebar = () => {
  const { t } = useTranslation("sidebar");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const prefersReducedMotion = useReducedMotion();
  const sidebarRef = useRef(null);
  const sidebarLinksRef = useRef(null);
  const submenuSnapParentsRef = useRef({});
  const submenuFallbackTimersRef = useRef({});

  const { user, navOptions, abacPermissions } = useSelector(authSelector);
  const { isMobile, isTablet } = useSelector(commonUtilitySelector);
  const isCompact = !!(isMobile || isTablet);
  const [isWindowMobile, setIsWindowMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const handleResize = () => {
      const isMob = window.innerWidth <= 768;
      setIsWindowMobile(isMob);
      if (!isMob) {
        setIsMobileOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobileView = !!(isMobile || isTablet || isWindowMobile);
  const profileData = globalThis.profileData || {};
  const { canViewPlatformDocuments, canAccess: canAccessAbacPath } =
    useAbacAccess();
  const canShowSdk = canAccessAbacPath(
    "platform_documents",
    "cpaas_business_api"
  );
  const canShowProfileNav = canAccessNavbarItem(
    abacPermissions,
    "profile",
    user,
    profileData
  );

  const [submenusOpen, setSubmenusOpen] = useState(() => {
    // Restore open/closed menus from localStorage (survive hard refresh)
    const saved = getItem("sideActive", true) || {};
    return { ...initialValue, ...saved };
  });
  const [shouldShowDlvSmsc, setShouldShowDlvSmsc] = useState(
    getItem("shouldShowDlvSmsc", true) ?? false
  );
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    getItem("sidebarCollapsed", true) ?? false
  );
  const [sidebarSearch, setSidebarSearch] = useState("");

  // Floating Viewport-Clamped Tooltip State
  const [tooltipState, setTooltipState] = useState({
    visible: false,
    text: "",
    targetRect: null,
  });

  // Anchored Flyout Submenu State
  const [activeFlyout, setActiveFlyout] = useState(null);
  const flyoutOpenTimerRef = useRef(null);
  const flyoutCloseTimerRef = useRef(null);

  const showFloatingTooltip = useCallback((text, targetEl) => {
    if (!targetEl || !text) return;
    setTooltipState({
      visible: true,
      text,
      targetRect: targetEl.getBoundingClientRect(),
    });
  }, []);

  const hideFloatingTooltip = useCallback(() => {
    setTooltipState((prev) => (prev.visible ? { ...prev, visible: false } : prev));
  }, []);

  const openFlyoutSubmenu = useCallback((flyoutData, immediate = false) => {
    if (flyoutCloseTimerRef.current) {
      clearTimeout(flyoutCloseTimerRef.current);
      flyoutCloseTimerRef.current = null;
    }
    if (immediate) {
      if (flyoutOpenTimerRef.current) {
        clearTimeout(flyoutOpenTimerRef.current);
        flyoutOpenTimerRef.current = null;
      }
      setTooltipState({ visible: false, text: "", targetRect: null });
      setActiveFlyout(flyoutData);
    } else {
      if (flyoutOpenTimerRef.current) clearTimeout(flyoutOpenTimerRef.current);
      flyoutOpenTimerRef.current = setTimeout(() => {
        flyoutOpenTimerRef.current = null;
        setTooltipState({ visible: false, text: "", targetRect: null });
        setActiveFlyout(flyoutData);
      }, 60); // 60ms debounce
    }
  }, []);

  const closeFlyoutSubmenu = useCallback((immediate = false) => {
    if (flyoutOpenTimerRef.current) {
      clearTimeout(flyoutOpenTimerRef.current);
      flyoutOpenTimerRef.current = null;
    }
    if (immediate) {
      if (flyoutCloseTimerRef.current) {
        clearTimeout(flyoutCloseTimerRef.current);
        flyoutCloseTimerRef.current = null;
      }
      setActiveFlyout(null);
    } else {
      if (flyoutCloseTimerRef.current) clearTimeout(flyoutCloseTimerRef.current);
      flyoutCloseTimerRef.current = setTimeout(() => {
        flyoutCloseTimerRef.current = null;
        setActiveFlyout(null);
      }, 180); // 180ms grace window
    }
  }, []);

  const cancelFlyoutClose = useCallback(() => {
    if (flyoutCloseTimerRef.current) {
      clearTimeout(flyoutCloseTimerRef.current);
      flyoutCloseTimerRef.current = null;
    }
  }, []);

  // Auto-expand matching module section on deep route navigation
  useEffect(() => {
    const currentPath = location.pathname || "";
    const matched = ROUTE_SECTION_MAP.find((m) => m.match(currentPath));
    if (matched) {
      setSubmenusOpen((prev) => {
        if (prev[matched.section]) return prev;
        const next = { ...prev, [matched.section]: true };
        storeItem("sideActive", next);
        return next;
      });
    }
  }, [location.pathname]);

  // Dismiss flyout and tooltip when sidebar is expanded
  useEffect(() => {
    if (!isCollapsed) {
      setActiveFlyout(null);
      setTooltipState({ visible: false, text: "", targetRect: null });
    }
  }, [isCollapsed]);

  const {
    pill,
    refreshPill,
    snapToElement,
    pinToElement,
    anchorPillToSubmenuParent,
    clearSubmenuPillAnchor,
    clearIntent,
  } = useSidebarActivePill(sidebarLinksRef, [
    location.pathname,
    isCollapsed,
    submenusOpen,
  ]);

  const reanchorAfterSubmenuClose = useCallback(
    (toggle) => {
      const parentEl = submenuSnapParentsRef.current[toggle];
      submenuSnapParentsRef.current[toggle] = null;
      if (!parentEl) return;

      clearSubmenuPillAnchor();
      if (parentEl instanceof Element) {
        pinToElement(parentEl);
      } else {
        refreshPill();
      }
    },
    [clearSubmenuPillAnchor, pinToElement, refreshPill]
  );

  const scheduleSubmenuCloseReanchor = useCallback(
    (toggle) => {
      if (submenuFallbackTimersRef.current[toggle]) {
        clearTimeout(submenuFallbackTimersRef.current[toggle]);
      }
      submenuFallbackTimersRef.current[toggle] = setTimeout(() => {
        submenuFallbackTimersRef.current[toggle] = null;
        reanchorAfterSubmenuClose(toggle);
      }, 260);
    },
    [reanchorAfterSubmenuClose]
  );

  const getNavOptionsDataOnce = () => {
    dispatch(getNavOptionsData()).then((action) =>
      middleware(action, "Nav Options")
    );
  };

  const toggleSubMenu = useCallback((submenu) => {
    setSubmenusOpen((prev) => {
      const newState = {
        ...prev,
        [submenu]: !prev[submenu],
      };
      storeItem("sideActive", newState);
      return newState;
    });
  }, []);

  const logout = () => {
    dispatch(logoutUser()).then((action) =>
      middleware(action, "Logout User", (action) => {
        dispatch(clearLogoutAuthState());
        clearAuthStorage();
        persistor.purge();
        document.body.classList.remove("dark-mode");
        navigate("/login");
        showToast(action.payload.message || "Logout successful", "success");
        globalThis.profileData = null;
      })
    );
  };

  const sidebarOptionOpen = () => {
    const pathname = window.location.pathname;
    const saved = getItem("sideActive", true) || {};
    const next = { ...initialValue, ...saved, ...submenusOpen };

    // Nested path menus: sync open state from route (sections keep saved state)
    Object.keys(next).forEach((submenu) => {
      if (!PATH_BASED_TOGGLES.has(submenu)) return;

      if (
        pathname.includes("/user/error-code") ||
        pathname.includes("/smsc/error-code")
      ) {
        next[submenu] = submenu === "errorCode";
        return;
      }

      if (submenu === "providers" || submenu === "smsc") {
        next[submenu] = pathname.includes("/smsc");
        return;
      }

      if (submenu === "routingSender") {
        next[submenu] = pathname.includes("/routing/sender");
        return;
      }

      if (submenu === "approvalSender") {
        next[submenu] = pathname.includes("/approvals/sender");
        return;
      }

      next[submenu] = pathname.includes(submenu);
    });

    // Keep section open/closed exactly as user left them (persist across refresh)
    SECTION_TOGGLES.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(saved, key)) {
        next[key] = !!saved[key];
      }
    });

    // Error Codes lives under Developers — keep that section open so active highlight is visible
    if (
      pathname.includes("/user/error-code") ||
      pathname.includes("/smsc/error-code")
    ) {
      next.developers = true;
      next.organizationAccounts =
        Object.prototype.hasOwnProperty.call(saved, "organizationAccounts")
          ? !!saved.organizationAccounts
          : next.organizationAccounts;
    }

    storeItem("sideActive", next);

    return next;
  };

  const getSmscList = () => {
    dispatch(getSmscNames()).then((action) =>
      middleware(action, "Message", (action) => {
        if (isArray(action.payload.data)) {
          setShouldShowDlvSmsc(action.payload.data.length > 0);
          storeItem("shouldShowDlvSmsc", action.payload.data.length > 0);
        }
      })
    );
  };

  useEffect(() => {
    setTimeout(() => {
      let activeElemet = document.querySelector(".link.active");
      activeElemet &&
        activeElemet.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 500);
  }, []);

  useEffect(() => {
    if (!isMultiAccessUser(user, profileData)) return;
    if (abacPermissions == null) return;
    if (!/^\/dashboard/i.test(String(location.pathname || ""))) return;
    if (
      isAbacRouteAllowed(
        location.pathname,
        abacPermissions,
        user,
        profileData
      )
    ) {
      return;
    }
    const fallback = findFirstAllowedAbacRoute(
      abacPermissions,
      user,
      profileData,
      { exclude: location.pathname }
    );
    if (fallback && fallback !== location.pathname) {
      navigate(fallback, { replace: true });
    }
    // No fallback → MainLayout hasNoAllowed / noLanding shows NoAccessPage.
    // Do not bounce to /dashboard (that left an empty sidebar + blank main).
  }, [
    location.pathname,
    abacPermissions,
    user,
    profileData,
    navigate,
  ]);

  useFetch(() => {
    if (hasRoles("admin,tenant", user)) getSmscList();
    setSubmenusOpen(sidebarOptionOpen());

    if (!isManager(user) && !isSeller(user)) {
      getNavOptionsDataOnce();
    }
  });

  // Error Codes must highlight under Developers — never leave Users selected
  useEffect(() => {
    const path = location.pathname || "";
    if (
      !path.startsWith("/user/error-code") &&
      !path.startsWith("/smsc/error-code")
    ) {
      return;
    }
    setSubmenusOpen((prev) => {
      if (prev.developers) return prev;
      const next = { ...prev, developers: true };
      storeItem("sideActive", next);
      return next;
    });
    clearIntent();
    requestAnimationFrame(() => refreshPill());
  }, [location.pathname, clearIntent, refreshPill]);

  const openMobileSidebar = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setIsMobileOpen(true);
  };

  const closeMobileSidebar = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setIsMobileOpen(false);
  };

  const closeSidebar = (e) => {
    e.stopPropagation();
    document.querySelector(".sidebarContainer").classList.toggle("show");
  };

  const toggleCollapse = (e) => {
    e.stopPropagation();
    setIsCollapsed((prev) => {
      const newValue = !prev;
      storeItem("sidebarCollapsed", newValue);
      if (newValue) {
        setSidebarSearch("");
        // Collapsed rail uses icon .active only — kill floating pill/shadow
        clearIntent();
      }
      return newValue;
    });
    // Remeasure after width transition so pill cannot sit at expanded size
    requestAnimationFrame(() => refreshPill());
  };

  const approvalsBulkBlock = (
    <LinkItem
      name={t("menu.bulkManagement", { defaultValue: "Bulk Management" })}
      to={"approvals/bulk"}
      customValidation={
        hasRoles("superadmin,system", user)
          ? true
          : someTrue(navOptions, "bulk_act,bulk_mgmt")
      }
      roleCondition={
        hasRoles("superadmin,system", user) ? undefined : "admin,tenant"
      }
      icon={icon("bulkManagement")}
      abacPath={[
        ["sms", "sms_utilities", "approvals", "bulk", "approve_reject"],
        ["sms", "sms_utilities", "approvals", "bulk", "log"],
      ]}
    />
  );

  const approvalsSenderBlock = (
    <LinkItem
      name={t("menu.senderIdManagement", {
        defaultValue: "Sender ID Management",
      })}
      to={"approvals/sender"}
      customValidation={
        hasRoles("superadmin,system", user)
          ? true
          : someTrue(navOptions, "sender_act,sender_mgmt")
      }
      roleCondition={
        hasRoles("superadmin,system", user) ? undefined : "admin,tenant"
      }
      icon={icon("senderIdManagement")}
      abacPath={[
        ["sms", "sms_utilities", "approvals", "sender", "approve_reject"],
        ["sms", "sms_utilities", "approvals", "sender", "log"],
      ]}
    />
  );

  return (
    <SidebarContext.Provider
      value={{
        user,
        navOptions,
        abacPermissions,
        profileData,
        submenusOpen,
        toggleSubMenu,
        isCollapsed,
        isCompact,
        sidebarSearch,
        snapToElement,
        clearIntent,
        refreshPill,
        prefersReducedMotion,
        submenuSnapParentsRef,
        scheduleSubmenuCloseReanchor,
        reanchorAfterSubmenuClose,
        anchorPillToSubmenuParent,
        showFloatingTooltip,
        hideFloatingTooltip,
        openFlyoutSubmenu,
        closeFlyoutSubmenu,
        cancelFlyoutClose,
        activeFlyout,
      }}
    >
      <div className={"sidebarContainer" + (isMobileView ? " d-none" : "")} onClick={closeSidebar}>
        <div
          className={
            "sidebar" +
            (isCollapsed ? " collapsed" : "") +
            (sidebarSearch.trim() ? " is-searching" : "")
          }
        >
          {/* Fixed top: profile + search + Dashboard (does not scroll) */}
          <div className="sidebar-top-card">
            <div
              className="profileHeader"
              onClick={() => {
                if (!canShowProfileNav) return;
                navigate("/profile");
              }}
              style={{ cursor: canShowProfileNav ? "pointer" : "default" }}
              {...getSidebarTooltipProps(
                globalThis.profileData?.userName ?? "Profile",
                { isCollapsed, isCompact }
              )}
            >
              <div className="profileHeader-avatar">
                {globalThis.profileData &&
                globalThis.profileData?.profileName &&
                globalThis.profileData?.profilePath ? (
                  <img
                    src={`data:image/${globalThis.profileData?.profileName};base64,${globalThis.profileData?.profilePath}`}
                    alt="userImg"
                    className="userImage prevent-dark-icon"
                    loading="lazy"
                  />
                ) : (
                  <img
                    src="/3d-avatar.jpg"
                    alt="user"
                    className="userImage prevent-dark-icon"
                    loading="lazy"
                  />
                )}
              </div>
              {!isSeller(user) && !isManager(user) ? (
                <div className="profileHeader-info">
                  <p
                    className="fs-18 fw-600 text-gray mb-0 lh-1 userNameDetail-name"
                    title={`${globalThis.profileData?.userName}`}
                  >
                    {globalThis.profileData?.userName ?? "N/A"}
                  </p>
                  <p className="fs-12 fw-600 text-light-gray mb-0">
                    {globalThis.profileData?.roles || "N/A"}
                  </p>
                </div>
              ) : (
                <div className="profileHeader-info">
                  <p
                    className="fs-18 fw-600 text-gray mb-0 lh-1 userNameDetail-name"
                    title={globalThis.profileData?.userName || "N/A"}
                  >
                    {globalThis.profileData?.userName || "N/A"}
                  </p>
                  <p className="fs-12 fw-600 text-light-gray mb-0">
                    {globalThis.profileData?.role || "N/A"}
                  </p>
                </div>
              )}
              <div
                className="closeBtn"
                onClick={(e) => {
                  e.stopPropagation();
                  closeSidebar(e);
                }}
              >
                <CloseButton />
              </div>
            </div>

            {!isCollapsed && (
              <div
                className="sidebar-search"
                onClick={(e) => e.stopPropagation()}
              >
                <IoIosSearch className="sidebar-search-icon" aria-hidden />
                <input
                  type="search"
                  className="sidebar-search-input"
                  placeholder={t("tooltips.search", {
                    defaultValue: "Search",
                  })}
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                  aria-label={t("tooltips.search", { defaultValue: "Search" })}
                />
              </div>
            )}

            {(!sidebarSearch.trim() ||
              t("menu.dashboard").toLowerCase().includes(sidebarSearch.trim().toLowerCase())) &&
              isAbacRouteAllowed(
                "/dashboard",
                abacPermissions,
                user,
                profileData,
                ["sms", "dashboard"]
              ) && (
              <div
                className="sidebar-top-dashboard"
                onClick={(e) => e.stopPropagation()}
                {...getSidebarTooltipProps(t("menu.dashboard"), {
                  isCollapsed,
                  isCompact,
                })}
              >
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    "sidebar-dashboard-link" +
                    (isActive ? " active sidebar-dashboard-link--selected" : "")
                  }
                  onClick={() => {
                    clearIntent();
                    refreshPill();
                  }}
                >
                  <span className="sidebar-dashboard-active-fill" aria-hidden />
                  <AppIcon name="dashboard" className="sidebar-dashboard-icon" />
                  {!isCollapsed && (
                    <span className="sidebar-dashboard-label">
                      {t("menu.dashboard")}
                    </span>
                  )}
                </NavLink>
              </div>
            )}
          </div>

          {/* Scrollable menu only — Logout stays fixed below */}
          <div className="sidebar-nav" ref={sidebarLinksRef}>
            <SidebarActivePill pill={pill} />
          {!(isSeller(user) || isManager(user)) ? (
            <div className="links">
              <MenuSection
                title={t("sections.customerEngagement", {
                  defaultValue: "Customer Engagement",
                })}
                icon="engagement"
                toggle="customerEngagement"
              >
                <LinkItem
                  name={t("menu.messages")}
                  to={"message"}
                  icon={icon("messages")}
                  navOption={"sms"}
                  abacPath={["sms", "message"]}
                />
                <LinkItem
                  name={t("menu.templates")}
                  to={"template"}
                  icon={icon("templates")}
                  navOption={"template"}
                  abacPath={["sms", "template"]}
                />
                <LinkItem
                  name={t("menu.twoWayMessaging", {
                    defaultValue: "Two-Way Messaging",
                  })}
                  to={"utilities/two-way"}
                  navOption={"twoWay"}
                  icon={icon("twoWayMessaging")}
                  abacPath={["sms", "sms_utilities", "two_way"]}
                />
                <LinkItem
                  name={t("menu.contacts", { defaultValue: "Contacts" })}
                  to={"address-book"}
                  icon={icon("contacts")}
                  navOption={"addBook"}
                  abacPath={["address_book"]}
                />
                <LinkItem
                  name={t("menu.optInPages", { defaultValue: "Opt-in Pages" })}
                  to={"utilities/subscription"}
                  icon={icon("optInPages")}
                  abacPath={["sms", "subscription"]}
                />
              </MenuSection>

              <MenuSection
                title={t("sections.vendorsProviders", {
                  defaultValue: "Vendors & Providers",
                })}
                icon="vendorsProviders"
                toggle="vendorsProviders"
              >
                <LinkItem
                  name={t("menu.vendors", { defaultValue: "Vendors" })}
                  roleCondition={"superadmin,system,tenant"}
                  icon={icon("vendors")}
                  to={"vendor"}
                  abacPath={["vendor"]}
                />
                <LinkItem
                  name={t("menu.providers", { defaultValue: "Providers" })}
                  icon={icon("providers")}
                  roleCondition={"superadmin,system,tenant"}
                  toggle={"providers"}
                  abacPath={["smsc"]}
                >
                  <LinkItem
                    name={t("menu.smscConnections", {
                      defaultValue: "SMSC Connections",
                    })}
                    icon={icon("smscConnections")}
                    to={"smsc/list"}
                    abacPath={["smsc", "list"]}
                  />
                  <LinkItem
                    name={t("menu.httpConnections", {
                      defaultValue: "HTTP Connections",
                    })}
                    to={"smsc/http"}
                    icon={icon("httpConnections")}
                    roleCondition={"superadmin,system,tenant"}
                    abacPath={["smsc", "http"]}
                  />
                  <LinkItem
                    name={t("menu.groupConnections", {
                      defaultValue: "Group Connections",
                    })}
                    icon={icon("groupConnections")}
                    to={"smsc/group-data"}
                    abacPath={["smsc", "groups"]}
                  />
                  <LinkItem
                    name={t("menu.hlrConnection", {
                      defaultValue: "HLR Connection",
                    })}
                    icon={icon("hlrConnection")}
                    to={"smsc/hlr"}
                    roleCondition={"superadmin,system,tenant"}
                    abacPath={["smsc", "hlr"]}
                  />
                  <LinkItem
                    name={t("menu.alerts", { defaultValue: "Alerts" })}
                    to={"smsc/alert"}
                    icon={icon("alerts")}
                    abacPath={["smsc", "alert"]}
                  />
                  <LinkItem
                    name={t("menu.trafficLimits", {
                      defaultValue: "Traffic Limits",
                    })}
                    to={"smsc/limits"}
                    icon={icon("trafficLimits")}
                    abacPath={["smsc", "limits"]}
                  />
                  <LinkItem
                    name={t("menu.looping")}
                    icon={icon("looping")}
                    to={"smsc/looping"}
                    abacPath={["smsc", "looping"]}
                  />
                  <LinkItem
                    name={t("menu.trafficBlocking", {
                      defaultValue: "Traffic Blocking",
                    })}
                    icon={icon("trafficBlocking")}
                    to={"smsc/blocking"}
                    abacPath={["smsc", "blocking"]}
                  />
                  <LinkItem
                    name={t("reports.schedule")}
                    icon={icon("schedule")}
                    to={"smsc/schedule"}
                    abacPath={["smsc", "schedule"]}
                  />
                  <LinkItem
                    name={t("menu.advancedSettings", {
                      defaultValue: "Advanced Settings",
                    })}
                    icon={icon("advancedSettings")}
                    to={"smsc/special-settings"}
                    abacPath={["smsc", "special_settings"]}
                  />
                </LinkItem>
              </MenuSection>

              <MenuSection
                title={t("sections.organizationAccounts", {
                  defaultValue: "Organization & Accounts",
                })}
                icon="organizationAccounts"
                toggle="organizationAccounts"
              >
                <LinkItem
                  name={t("menu.organizations", {
                    defaultValue: "Organizations",
                  })}
                  roleCondition={"superadmin,system,admin,tenant,seller,manager"}
                  icon={icon("organizations")}
                  to={"company"}
                  abacPath={["company"]}
                />
                <LinkItem
                  name={t("menu.users", { defaultValue: "Users" })}
                  to={"user/list"}
                  icon={icon("users")}
                  navOption={"user"}
                  roleCondition={"admin,tenant,superadmin,system"}
                  abacPath={[
                    ["user", "profile"],
                    ["user", "limit"],
                  ]}
                  isNavActive={isUsersModulePath}
                />
                <LinkItem
                  name={t("menu.resellers", { defaultValue: "Resellers" })}
                  to={"sales"}
                  icon={icon("resellers")}
                  navOption={"sales"}
                  roleCondition={"admin,tenant,superadmin,system"}
                  abacPath={["sales_team"]}
                />
              </MenuSection>

              <MenuSection
                title={t("sections.messageOperations", {
                  defaultValue: "Message Operations",
                })}
                icon="messageOperations"
                toggle="messageOperations"
              >
                <LinkItem
                  name={t("menu.dltCompliance", {
                    defaultValue: "DLT Compliance",
                  })}
                  to={"dlt"}
                  icon={icon("dltCompliance")}
                  roleCondition={"superadmin,system"}
                  abacPath={[
                    ["dlt", "principle"],
                    ["dlt", "template"],
                  ]}
                />
                {approvalsBulkBlock}
                {approvalsSenderBlock}
              </MenuSection>

              <MenuSection
                title={t("sections.analyticsReports", {
                  defaultValue: "Analytics & Reports",
                })}
                icon="analyticsReports"
                toggle="analyticsReports"
              >
                <LinkItem
                  name={t("reports.campaign")}
                  toggle={"campaign"}
                  icon={icon("campaignManager")}
                  navOption={"report"}
                  abacPath={["sms", "reports", "campaign"]}
                >
                  <LinkItem
                    name={t("reports.aborted")}
                    to={"reports/campaign/aborted"}
                    icon={icon("aborted")}
                    abacPath={["sms", "reports", "campaign", "aborted"]}
                  />
                  <LinkItem
                    name={t("reports.campaign")}
                    to={"reports/campaign/campaign"}
                    icon={icon("campaignReport")}
                    abacPath={["sms", "reports", "campaign", "campaign"]}
                  />
                  <LinkItem
                    name={t("reports.schedule")}
                    to={"reports/campaign/schedule"}
                    icon={icon("scheduleAlt")}
                    abacPath={["sms", "reports", "campaign", "schedule"]}
                  />
                </LinkItem>
                <LinkItem
                  name={t("reports.summary")}
                  icon={icon("summary")}
                  toggle={"summary"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "summary"]}
                >
                  <LinkItem
                    name={t("reports.delivery")}
                    icon={icon("delivery")}
                    to={"reports/summary/delivery"}
                    abacPath={["sms", "reports", "summary", "delivery"]}
                  />
                  <LinkItem
                    name={t("reports.batch")}
                    icon={icon("batch")}
                    to={"reports/summary/batch"}
                    abacPath={["sms", "reports", "summary", "batch"]}
                  />
                </LinkItem>
                <LinkItem
                  name={t("reports.detailedAnalytics")}
                  icon={icon("detailedAnalytics")}
                  toggle={"detailed"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "detailed_analytics"]}
                >
                  <LinkItem
                    name={t("reports.deliveryAdvance")}
                    icon={icon("deliveryAdvance")}
                    to={"reports/detailed/advanced"}
                    abacPath={[
                      "sms",
                      "reports",
                      "detailed_analytics",
                      "delivery_advance",
                    ]}
                  />
                  <LinkItem
                    name={t("reports.deliveryContent")}
                    icon={icon("deliveryContent")}
                    to={"reports/detailed/content"}
                    abacPath={[
                      "sms",
                      "reports",
                      "detailed_analytics",
                      "delivery_content",
                    ]}
                  />
                  <LinkItem
                    name={t("reports.deliverySmsc")}
                    to={"reports/detailed/smsc"}
                    icon={icon("smscConnections")}
                    roleCondition={"admin,tenant"}
                    customValidation={shouldShowDlvSmsc}
                    abacPath={[
                      "sms",
                      "reports",
                      "detailed_analytics",
                      "delivery_smsc",
                    ]}
                  />
                </LinkItem>
                <LinkItem
                  name={t("reports.deliveryLatency")}
                  icon={icon("deliveryLatency")}
                  to={"reports/latency"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "delivery_latency"]}
                />
                <LinkItem
                  name={t("reports.consumption")}
                  icon={icon("consumption")}
                  to={"reports/consumption"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "consumption"]}
                />
                <LinkItem
                  name={t("reports.lookup")}
                  icon={icon("lookUp")}
                  to={"reports/lookup"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "lookup"]}
                />
                <LinkItem
                  name={t("reports.aggregator")}
                  to={"reports/aggregated"}
                  icon={icon("aggregator")}
                  roleCondition={"superadmin,admin,tenant,system"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "aggregator"]}
                />
                <LinkItem
                  name={t("reports.profit")}
                  to={"reports/profit"}
                  icon={icon("profit")}
                  roleCondition={"superadmin,admin,tenant,system"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "profit"]}
                />
                <LinkItem
                  name={t("reports.performance")}
                  toggle={"performance"}
                  icon={icon("performance")}
                  roleCondition={"superadmin,system,tenant"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "performance"]}
                >
                  <LinkItem
                    name={t("menu.user")}
                    icon={icon("user")}
                    to={"reports/performance/user"}
                    abacPath={["sms", "reports", "performance", "user"]}
                  />
                  <LinkItem
                    name={t("reports.route")}
                    icon={icon("route")}
                    to={"reports/performance/route"}
                    abacPath={["sms", "reports", "performance", "route"]}
                  />
                </LinkItem>
                <LinkItem
                  name={t("reports.blocked")}
                  to={"reports/blocked"}
                  icon={icon("blocked")}
                  roleCondition={"superadmin,system"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "blocked"]}
                />
                <LinkItem
                  name={t("reports.tps")}
                  to={"reports/tps"}
                  icon={icon("tps")}
                  roleCondition={"admin,tenant,system,superadmin"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "tps"]}
                />
                <LinkItem
                  name={t("reports.vendorSummary")}
                  to={"reports/vendor-summary"}
                  icon={icon("vendorSummary")}
                  roleCondition={"admin,tenant,system,superadmin"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "vendor_summary"]}
                />
                <LinkItem
                  name={t("reports.edr")}
                  to={"reports/edr"}
                  icon={icon("edr")}
                  roleCondition={"admin,tenant,system,superadmin"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "edr"]}
                />
                <LinkItem
                  name={t("reports.coverage")}
                  icon={icon("coverage")}
                  to={"reports/coverage-report"}
                  roleCondition={"superadmin,system,tenant"}
                  navOption={"report"}
                  abacPath={["sms", "reports", "coverage"]}
                />
                <LinkItem
                  name={t("reports.coverage")}
                  to={"reports/coverage-report"}
                  roleCondition={"admin"}
                  icon={icon("coverage")}
                  navOption={"report"}
                  customValidation={
                    !shouldShowDlvSmsc && !checkUserRole(user, "tenant")
                  }
                  abacPath={["sms", "reports", "coverage"]}
                />
              </MenuSection>

              <MenuSection
                title={t("sections.complianceSecurity", {
                  defaultValue: "Compliance & Security",
                })}
                icon="complianceSecurity"
                toggle="complianceSecurity"
              >
                <LinkItem
                  name={t("menu.firewall")}
                  to={"firewall"}
                  icon={icon("firewall")}
                  roleCondition={"admin,tenant,system,superadmin"}
                  navOption={"bsfm"}
                  abacPath={["sms", "firewall"]}
                />
                <LinkItem
                  name={t("menu.networks", { defaultValue: "Networks" })}
                  to={"network"}
                  icon={icon("networks")}
                  navOption={"sales"}
                  roleCondition={"superadmin,system"}
                  abacPath={["network"]}
                />
                <LinkItem
                  name={t("menu.pcap")}
                  to={"utilities/pcaps"}
                  roleCondition={"superadmin,system,tenant"}
                  icon={icon("pcap")}
                  abacPath={["sms", "sms_utilities", "pcap"]}
                />
              </MenuSection>

              <MenuSection
                title={t("sections.billingRouting", {
                  defaultValue: "Billing & Routing",
                })}
                icon="billingPricing"
                toggle="billingRouting"
              >
                <LinkItem
                  name={t("menu.routing")}
                  to={
                    hasRoles("tenant,superadmin,system", user)
                      ? "routing/add"
                      : "routing/edit"
                  }
                  icon={icon("routing")}
                  roleCondition={"superadmin,system,admin,tenant"}
                  navOption={"routing"}
                  abacPath={ROUTING_MENU_ACCESS_PATHS}
                  isNavActive={(pathname) =>
                    pathname.startsWith("/routing") &&
                    !pathname.startsWith("/routing/sender")
                  }
                />
                <LinkItem
                  name={t("menu.senderBasedRouting", {
                    defaultValue: "Sender-Based Routing",
                  })}
                  to={"routing/sender/add"}
                  roleCondition={"superadmin,system,tenant"}
                  icon={icon("senderBasedRouting")}
                  navOption={"routing"}
                  abacPath={ROUTING_SENDER_ACCESS_PATHS}
                  isNavActive={(pathname) =>
                    pathname.startsWith("/routing/sender")
                  }
                />
              </MenuSection>

              <MenuSection
                title={t("sections.developers", {
                  defaultValue: "Developers",
                })}
                icon="developerTools"
                toggle="developers"
              >
                <LinkItem
                  name={t("menu.errorCodes")}
                  to={"user/error-code"}
                  icon={icon("errorCodes")}
                  abacPath={[
                    ["error_code", "user"],
                    ["error_code", "smsc"],
                  ]}
                  isNavActive={isErrorCodesPath}
                />
                <LinkItem
                  name={t("menu.encodingTools", {
                    defaultValue: "Encoding Tools",
                  })}
                  to={"utilities/converter"}
                  icon={icon("encodingTools")}
                  abacPath={["sms", "sms_utilities", "character_converter"]}
                />
                <LinkItem
                  name={t("menu.sdks", { defaultValue: "SDKs" })}
                  to={"developer/sdk"}
                  icon={icon("sdk")}
                  customValidation={canShowSdk}
                />
                <LinkItem
                  name={t("menu.documents", { defaultValue: "Documentation" })}
                  to={"downloads"}
                  icon={icon("documentation")}
                  customValidation={canViewPlatformDocuments}
                />
              </MenuSection>
            </div>
          ) : (
            <div className="links">
              <MenuSection
                title={t("sections.analyticsReports", {
                  defaultValue: "Analytics & Reports",
                })}
                icon="analyticsReports"
                toggle="analyticsReports"
              >
                <LinkItem
                  name={t("seller.batch")}
                  to={"reports/batch-summary"}
                  icon={icon("batch")}
                  abacPath={["sms", "reports", "summary", "batch"]}
                />
                <LinkItem
                  name={t("seller.submission")}
                  to={"reports/submission"}
                  icon={icon("summary")}
                />
                <LinkItem
                  name={t("reports.delivery")}
                  to={"reports/delivery-advance"}
                  icon={icon("deliveryAdvance")}
                  abacPath={[
                    "sms",
                    "reports",
                    "detailed_analytics",
                    "delivery_advance",
                  ]}
                />
                <LinkItem
                  name={t("reports.coverage")}
                  to={"reports/coverage"}
                  icon={icon("coverage")}
                  abacPath={["sms", "reports", "coverage"]}
                />
                <LinkItem
                  name={t("seller.performanceUser")}
                  to={"reports/performance-user"}
                  icon={icon("performance")}
                  abacPath={["sms", "reports", "performance", "user"]}
                />
                <LinkItem
                  name={t("seller.transactionHistory")}
                  to={"reports/transaction-history"}
                  icon={icon("consumption")}
                />
              </MenuSection>
              <MenuSection
                title={t("sections.organizationAccounts", {
                  defaultValue: "Organization & Accounts",
                })}
                icon="organizationAccounts"
                toggle="organizationAccounts"
              >
                <LinkItem
                  name={t("menu.organizations", {
                    defaultValue: "Organizations",
                  })}
                  to={"company"}
                  icon={icon("organizations")}
                  abacPath={["company"]}
                />
                <LinkItem
                  name={t("menu.resellers", { defaultValue: "Resellers" })}
                  to={"sales-list"}
                  icon={icon("resellers")}
                  roleCondition={"manager"}
                  abacPath={["sales_team"]}
                />
                <LinkItem
                  name={t("seller.user")}
                  to={"reports/user-status"}
                  icon={icon("users")}
                />
              </MenuSection>
              <MenuSection
                title={t("sections.customerEngagement", {
                  defaultValue: "Customer Engagement",
                })}
                icon="engagement"
                toggle="customerEngagement"
              >
                <LinkItem
                  name={t("seller.view")}
                  toggle={"view"}
                  icon={icon("messages")}
                  abacPath={[
                    ["sms", "sms_utilities", "schedule_manager"],
                    ["sms", "sms_utilities", "campaign_manager"],
                  ]}
                >
                  <LinkItem
                    name={t("reports.schedule")}
                    icon={icon("schedule")}
                    to={"view/schedule"}
                    abacPath={["sms", "sms_utilities", "schedule_manager"]}
                  />
                  <LinkItem
                    name={t("menu.bulk")}
                    icon={icon("bulkManagement")}
                    to={"view/bulk"}
                    abacPath={["sms", "sms_utilities", "campaign_manager"]}
                  />
                </LinkItem>
              </MenuSection>
              <MenuSection
                title={t("sections.developers", {
                  defaultValue: "Developers",
                })}
                icon="developerTools"
                toggle="developers"
              >
                <LinkItem
                  name={t("menu.sdks", { defaultValue: "SDKs" })}
                  to={"developer/sdk"}
                  icon={icon("sdk")}
                  customValidation={canShowSdk}
                />
                <LinkItem
                  name={t("menu.documents", { defaultValue: "Documentation" })}
                  to={"downloads"}
                  icon={icon("documentation")}
                  customValidation={canViewPlatformDocuments}
                />
              </MenuSection>
            </div>
          )}
          </div>
          <div className="logout">
            <p
              onClick={logout}
              {...getSidebarTooltipProps(t("menu.logout"), {
                isCollapsed,
                isCompact,
              })}
            >
              {icon("logout")}
              <span className="link-title w-100">{t("menu.logout")}</span>
            </p>
          </div>
        </div>
        <button
          type="button"
          className="collapseBtn"
          onClick={toggleCollapse}
          data-tooltip={
            isCollapsed
              ? t("tooltips.expandSidebar", {
                  defaultValue: "Expand sidebar",
                })
              : t("tooltips.collapseSidebar", {
                  defaultValue: "Collapse sidebar",
                })
          }
          data-tooltip-location="right"
          aria-label={
            isCollapsed
              ? t("tooltips.expandSidebar", {
                  defaultValue: "Expand sidebar",
                })
              : t("tooltips.collapseSidebar", {
                  defaultValue: "Collapse sidebar",
                })
          }
        >
          <img
            src={isCollapsed ? openSidebarIcon : closeSidebarIcon}
            alt=""
            loading="lazy"
          />
        </button>
      </div>
      {/* Mobile Hamburger Toggle Button */}
      {isMobileView && !isMobileOpen && (
        <div className="mobToggle" onClick={openMobileSidebar}>
          <button
            type="button"
            aria-label={t("tooltips.toggleSidebar", { defaultValue: "Toggle Sidebar" })}
          >
            <img src={mobToggleIcon} alt="Open sidebar" loading="lazy" />
          </button>
        </div>
      )}

      {/* Production-Grade Mobile Navigation Drawer */}
      <MobileNavDrawer
        isOpen={isMobileOpen}
        onClose={closeMobileSidebar}
        user={user}
        profileData={profileData}
        abacPermissions={abacPermissions}
        logout={logout}
      />
    
      <SidebarFloatingTooltip
        visible={tooltipState.visible}
        text={tooltipState.text}
        targetRect={tooltipState.targetRect}
        sidebarRect={sidebarRef.current?.getBoundingClientRect()}
        isSuppressed={!isCollapsed || !!activeFlyout || isMobileView}
      />
      <SidebarFlyoutSubmenu
        activeFlyout={activeFlyout}
        sidebarRect={sidebarRef.current?.getBoundingClientRect()}
        onClose={() => closeFlyoutSubmenu(true)}
        onMouseEnterFlyout={cancelFlyoutClose}
        onMouseLeaveFlyout={() => closeFlyoutSubmenu(false)}
        isCollapsed={isCollapsed && !isMobileView}
      />
    </SidebarContext.Provider>
  );
};

export default Sidebar;
